import { COVER_CROP_MIX_TYPE, ECONOMICS_FIELDS, SCENARIO_LIMITS } from "consts";

import { SoilSightCropDefaultsByCropId } from "models";

import { CropYear, ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { coverCropDefaultsKey } from "pages/app/scenario-analysis/utils/cropDetailsDefaults";
import { createEmptyYear, growsCoverCrop, pruneEmptyCropYears } from "pages/app/scenario-analysis/utils/cropYearHelpers";

/** The scaffold a new project starts from: the reference rotation with four empty years. */
export const buildDefaultScenarios = (referenceTitle: string): ScenarioData[] => [
  {
    id: "baseline",
    title: referenceTitle,
    isBaseline: true,
    isReference: true,
    years: [createEmptyYear("year-1"), createEmptyYear("year-2"), createEmptyYear("year-3"), createEmptyYear("year-4")],
  },
];

export const withUpdatedScenarioTitle = (scenarios: ScenarioData[], scenarioId: string, title: string): ScenarioData[] =>
  scenarios.map((scenario) => (scenario.id === scenarioId ? { ...scenario, title } : scenario));

/** Strips each rotation's blank scaffold rows before a run, never down to zero rows (the "no crop added" check catches that). */
export const withEmptyYearsPruned = (scenarios: ScenarioData[]): ScenarioData[] =>
  scenarios.map((scenario) => ({ ...scenario, years: pruneEmptyCropYears(scenario.years) }));

export const withRemovedScenario = (scenarios: ScenarioData[], scenarioId: string): ScenarioData[] =>
  scenarios.filter((scenario) => scenario.id !== scenarioId);

export const withUpdatedYearData = (scenarios: ScenarioData[], scenarioId: string, yearId: string, updates: Partial<CropYear>): ScenarioData[] =>
  scenarios.map((scenario) => {
    if (scenario.id !== scenarioId) return scenario;
    return {
      ...scenario,
      years: scenario.years.map((year) => {
        if (year.id !== yearId) return year;
        if ("crop" in updates && !updates.crop?.trim()) {
          return {
            ...year,
            crop: "",
            coverCropId: COVER_CROP_MIX_TYPE.NONE,
            tillageId: null,
            residue: null,
            irrigation: false,
            legumeShare: null,
            ccCost: null,
          };
        }
        return { ...year, ...updates };
      }),
    };
  });

export const withRemovedOrClearedYear = (scenarios: ScenarioData[], scenarioId: string, yearId: string): ScenarioData[] =>
  scenarios.map((scenario) => {
    if (scenario.id !== scenarioId) return scenario;
    const yearIndex = scenario.years.findIndex((year) => year.id === yearId);
    if (yearIndex === -1) return scenario;
    if (scenario.years.length > SCENARIO_LIMITS.MIN_YEARS) {
      return { ...scenario, years: scenario.years.filter((year) => year.id !== yearId) };
    }
    return { ...scenario, years: scenario.years.map((year) => (year.id === yearId ? createEmptyYear(year.id) : year)) };
  });

export const withSavedYear = (
  scenarios: ScenarioData[],
  scenarioId: string,
  yearId: string,
  updatedYear: CropYear
): { scenarios: ScenarioData[]; years: CropYear[] } => {
  let years: CropYear[] = [];
  // PUT /crop-years returns { success: true } only. Returning the full updated crop years
  // would increase network load — this endpoint fires on every table-row change via a
  // 500ms debounced autosave, so the API is intentionally kept generic. We stamp
  // Date.now() here so the just-saved year wins the within-session inheritance priority
  // check when another drawer opens for the same crop before the next page load.
  const stamped = { ...updatedYear, updatedAt: Date.now() };
  const updatedScenarios = scenarios.map((scenario) => {
    if (scenario.id !== scenarioId) return scenario;
    const updated = scenario.years.map((year) => (year.id === yearId ? { ...year, ...stamped } : year));
    years = updated;
    return { ...scenario, years: updated };
  });

  return { scenarios: updatedScenarios, years };
};

export const withAppliedYearToAllMatchingCrops = (
  scenarios: ScenarioData[],
  scenarioId: string,
  yearId: string,
  updatedYear: CropYear
): { scenarios: ScenarioData[]; affectedScenarios: Array<{ id: string; years: CropYear[] }> } => {
  const sourceScenario = scenarios.find((s) => s.id === scenarioId);
  const sourceYear = sourceScenario?.years.find((y) => y.id === yearId);
  const targetCrop = updatedYear.crop || sourceYear?.crop;

  if (!targetCrop) return { scenarios, affectedScenarios: [] };

  // Same reason as withSavedYear: client-side stamp for within-session inheritance ordering.
  const stamped: CropYear = { ...updatedYear, updatedAt: Date.now() };
  const affectedScenarios: Array<{ id: string; years: CropYear[] }> = [];
  const updatedScenarios = scenarios.map((scenario) => {
    const hasMatchingCrop = scenario.years.some((y) => y.crop === targetCrop);
    if (!hasMatchingCrop) return scenario;
    const updated = scenario.years.map((year) => (year.crop === targetCrop ? { ...year, ...stamped, id: year.id } : year));
    affectedScenarios.push({ id: scenario.id, years: updated });
    return { ...scenario, years: updated };
  });

  return { scenarios: updatedScenarios, affectedScenarios };
};

export const getIncompleteYearIds = (scenarios: ScenarioData[], scenarioId: string): string[] => {
  const scenario = scenarios.find((item) => item.id === scenarioId);
  if (!scenario) return [];
  return scenario.years.filter((year) => !year.crop).map((year) => year.id);
};

export const withAddedYear = (scenarios: ScenarioData[], scenarioId: string, yearId: string): ScenarioData[] =>
  scenarios.map((scenario) => (scenario.id === scenarioId ? { ...scenario, years: [...scenario.years, createEmptyYear(yearId)] } : scenario));

export const withReorderedYears = (scenarios: ScenarioData[], scenarioId: string, fromIndex: number, toIndex: number): ScenarioData[] =>
  scenarios.map((scenario) => {
    if (scenario.id !== scenarioId) return scenario;
    const years = [...scenario.years];
    const [moved] = years.splice(fromIndex, 1);
    years.splice(toIndex, 0, moved);
    return { ...scenario, years };
  });

/**
 * For each cropId across all scenarios, finds the most-recently drawer-saved (or API-loaded)
 * year that has at least one non-null economics field, and returns its economics values keyed
 * by cropId. The `excludeYearId` is skipped so a year doesn't inherit from itself.
 *
 * Priority: years with a higher `updatedAt` win (drawer-saves in the current session beat
 * API-loaded rows with older timestamps, and API-loaded rows beat years with no timestamp).
 */
export const buildInheritedDefaultsByCropId = (scenarios: ScenarioData[], excludeYearId: string): SoilSightCropDefaultsByCropId => {
  const candidates = new Map<string, { updatedAt: number; year: CropYear }>();

  for (const scenario of scenarios) {
    for (const year of scenario.years) {
      if (year.id !== excludeYearId && year.crop) {
        const hasData = ECONOMICS_FIELDS.some((f) => year[f] != null);
        if (hasData) {
          const updatedAt = year.updatedAt ?? 0;
          const existing = candidates.get(year.crop);
          if (!existing || updatedAt > existing.updatedAt) {
            candidates.set(year.crop, { updatedAt, year });
          }
        }
      }
    }
  }

  const result: SoilSightCropDefaultsByCropId = {};
  for (const [cropId, { year }] of candidates) {
    result[cropId] = {
      yield: year.yield ?? null,
      sellingPrice: year.sellingPrice ?? null,
      fodderLosses: year.fodderLosses ?? null,
      mouldLosses: year.mouldLosses ?? null,
      minFertiliser: year.minFertiliser ?? null,
      plantProtection: year.plantProtection ?? null,
      seedCosts: year.seedCosts ?? null,
      seedAmount: year.seedAmount ?? null,
    };
  }

  return result;
};

/**
 * For each crop+coverCropType combination across all scenarios, finds the most-recently
 * updated year that has at least one non-null cover crop field (legumeShare or ccCost),
 * and returns its values keyed by `${cropId}::${coverCropType}`.
 *
 * Same priority logic as buildInheritedDefaultsByCropId: higher updatedAt wins.
 * The excludeYearId is skipped so a year doesn't inherit from itself.
 */
export const buildInheritedCoverCropDefaultsByKey = (
  scenarios: ScenarioData[],
  excludeYearId: string
): Record<string, { legumeShare: number | null; ccCost: number | null }> => {
  const candidates = new Map<string, { updatedAt: number; year: CropYear }>();

  for (const scenario of scenarios) {
    for (const year of scenario.years) {
      if (year.id !== excludeYearId && year.crop && growsCoverCrop(year)) {
        const hasCoverData = year.legumeShare != null || year.ccCost != null;
        if (hasCoverData) {
          const key = coverCropDefaultsKey(year.crop, year.coverCropId);
          const updatedAt = year.updatedAt ?? 0;
          const existing = candidates.get(key);
          if (!existing || updatedAt > existing.updatedAt) {
            candidates.set(key, { updatedAt, year });
          }
        }
      }
    }
  }

  const result: Record<string, { legumeShare: number | null; ccCost: number | null }> = {};
  for (const [key, { year }] of candidates) {
    result[key] = { legumeShare: year.legumeShare ?? null, ccCost: year.ccCost ?? null };
  }

  return result;
};

export const buildLocalScenario = (scenarios: ScenarioData[], title: string, copyFromScenarioId?: string): ScenarioData => {
  const newScenarioId = `scenario-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const sourceScenario = copyFromScenarioId ? scenarios.find((scenario) => scenario.id === copyFromScenarioId) : null;

  const years =
    sourceScenario?.years.map((year, index) => ({ ...year, id: `year-${newScenarioId}-${index}` })) ??
    Array.from({ length: SCENARIO_LIMITS.MIN_YEARS }, (_, index) => createEmptyYear(`year-${newScenarioId}-${index}`));

  return {
    id: newScenarioId,
    title,
    isBaseline: false,
    isReference: false,
    years,
  };
};
